import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import _ from 'lodash';
import shortid from 'shortid';

import API from 'api';
import { getInstruction } from 'activity-templates/utils';
import { getScale, extractContent } from 'utils';
import { getMobileAppPlatform } from 'utils/isMobileAppRuntime';
import { resolveOfflineAssetUrlSync } from 'mobile-modules/offline';
import {
  isMobileLocalFriend,
  updateMobileFamilyFriend,
} from 'utils/mobileFamilyFriends';
import {
  Instructions,
  ActivityButtons,
  WhiteBox,
  PlayRecorder,
  WritingFeedback,
} from 'components/flink-play';
import MathRodsGameModal from './MathRodsGameModal';
import { GO_BACK, SPEAK, HELP, EBOOK_BUTTON, STOP, ACTIVITY_IMAGE } from 'consts/buttons';
import { activitiesKey, usersKey, uploadsURL, imagesURL } from 'config';
import {
  EDITOR_AND_RECORDER,
  EDITOR_ONLY,
  RECORDER_ONLY,
} from 'consts/story-studio-consts';
import { playAudio, stopAudio } from 'actions/audioActions';
import { showStats } from 'actions/gameActions';
import { closeWritingFeedback } from 'actions/flinkPlayActions';

import StoryStudioEditor from '../story-studio/StoryStudioEditor/StoryStudioEditor';
import PagesListBox from '../story-studio/PagesListBox/PagesListBox';
import PictureBox from './MathRodsPictureBox';
import { STORY_STUDIO } from 'consts/activity-templates';

import classes from '../story-studio/StoryStudio.module.scss';

const STORY_STUDIO_PHONE_WIDTH = 994;
const STORY_STUDIO_PHONE_HEIGHT = 450;
const STORY_STUDIO_PHONE_WORK_AREA_HEIGHT = 429;
const STORY_STUDIO_PHONE_PICTURE_HEIGHT_WITH_RECORDER = 369;
const STORY_STUDIO_PHONE_INITIAL_MAX_SCALE = 0.52;
const STORY_STUDIO_PHONE_MAX_SCALE = 0.82;
const STORY_STUDIO_PHONE_GLOBAL_BAR_RESERVE = 60;
const STORY_STUDIO_PHONE_PAGES_PANEL_WIDTH = 96;
const STORY_STUDIO_PHONE_PANEL_GAP = 12;
const STORY_STUDIO_ANDROID_HEIGHT_REDUCTION = 5;
const STORY_STUDIO_ANDROID_TOP_OFFSET = 5;

const getViewportSize = () => {
  if (typeof window === 'undefined') return { width: 0, height: 0 };

  const widths = [
    window.visualViewport?.width,
    document.documentElement?.clientWidth,
    window.innerWidth,
  ].filter((value) => Number(value) > 0);
  const heights = [
    window.visualViewport?.height,
    document.documentElement?.clientHeight,
    window.innerHeight,
  ].filter((value) => Number(value) > 0);

  return {
    width: widths.length ? Math.min(...widths) : 0,
    height: heights.length ? Math.min(...heights) : 0,
  };
};

const isNativePhoneViewport = (mobileLayout = false) => {
  if (!mobileLayout || typeof window === 'undefined') return false;

  const viewport = getViewportSize();
  const viewportShortSide = Math.min(viewport.width || Infinity, viewport.height || Infinity);
  const screenShortSide = Math.min(
    Number(window.screen?.width) || Infinity,
    Number(window.screen?.height) || Infinity,
  );
  const shortSide = Math.min(viewportShortSide, screenShortSide);

  return Number.isFinite(shortSide) && shortSide > 0 && shortSide <= 700;
};

const getStoryStudioPhoneScale = (viewportReady = false, viewportOverride = null) => {
  const viewport = viewportOverride || getViewportSize();
  const maxScale = viewportReady
    ? STORY_STUDIO_PHONE_MAX_SCALE
    : STORY_STUDIO_PHONE_INITIAL_MAX_SCALE;

  if (!viewport.width || !viewport.height) return STORY_STUDIO_PHONE_INITIAL_MAX_SCALE;

  const widthScale = (viewport.width - 16) / STORY_STUDIO_PHONE_WIDTH;
  const heightScale = (
    viewport.height - STORY_STUDIO_PHONE_GLOBAL_BAR_RESERVE
  ) / STORY_STUDIO_PHONE_HEIGHT;

  return Math.max(
    0.42,
    Math.min(maxScale, widthScale, heightScale)
  );
};

const getStoryStudioEbookPhoneScale = (viewportReady = false) => {
  const maxScale = viewportReady
    ? STORY_STUDIO_PHONE_MAX_SCALE
    : STORY_STUDIO_PHONE_INITIAL_MAX_SCALE;

  return Math.min(maxScale, getScale());
};

const divideSize = (s) => s / 2 + 'px';
const prevBtnImg = `${imagesURL}/TemplateGraphics/e-Book/arrow_left.png`;
const nextBtnImg = `${imagesURL}/TemplateGraphics/e-Book/arrow_right.png`;

const defaultData = {
  templateAlias: STORY_STUDIO,
  pages: [],
};


const getLocaleFromVersionType = (versionType = '') => {
  const normalizedVersionType = String(versionType || '').trim();
  const separatorIndex = normalizedVersionType.indexOf('_');

  return separatorIndex === -1 ? '' : normalizedVersionType.slice(separatorIndex + 1);
};

const hasFilledTextItems = (items = []) => (
  Array.isArray(items) && items.some(item => String(item || '').trim())
);

const hasStoryStudioLocaleData = (data = {}) => {
  if (!data || typeof data !== 'object') return false;

  return hasFilledTextItems(data.words)
    || (Array.isArray(data.images) && data.images.length > 0)
    || (Array.isArray(data.pages) && data.pages.length > 0)
    || Number(data.pagesCount) > 0;
};

export class StoryStudio extends Component {
      constructor(props) {
      super(props);
  
      const { activity, learnerId, team } = props;
      const {
          data: {
              options: { storyStudioMode },
          },
          activity: { _id: activityId },
      } = activity;
  
      const explicitVersionType = props.versionType || '';
      const explicitLocale = getLocaleFromVersionType(explicitVersionType);
      const locale = props.activity.activity?.monolingual
        ? ''
        : explicitLocale
          || props.activity.activity?.currentLanguageVersion?.locale
          || props?.solutionLocale?.code
          || '';
      const versionType = explicitVersionType || ((!locale || locale === 'en') ? '' : `immersion_${locale}`);
      const immersionData = versionType
        ? props.activity.data.languageVersionData?.[versionType]
        : null;
      const useImmersion = hasStoryStudioLocaleData(immersionData);
      const gameData = useImmersion ? immersionData : props.activity.data.gameData || {};
      const activityAssetsKey = useImmersion
        ? `${activitiesKey}/${activityId}/languageVersionData/${versionType}`
        : `${activitiesKey}/${activityId}/gamedata`;
      const activityAssetsFolder = `${uploadsURL}/${activityAssetsKey}/`;


      const {
          autoInsertArt,
          images = [],
          words = [],
          pagesCount: pagesCountOrigin,
      } = gameData;
  
      const filteredWords = words.filter((w) => !!(w && w.trim()) && w);
      const filteredImages = (images || []).filter((i) => !!i);
      const pagesCount = autoInsertArt ? filteredImages.length : pagesCountOrigin;
  
      const learnersIDs = [];
      const localFriendIds = [];
  
      if (learnerId) {
          learnersIDs.push(learnerId);
      }
  
      if (team) {
          learnersIDs.push(
            ...team.filter((member) => !isMobileLocalFriend(member)).map((member) => member._id),
          );
          localFriendIds.push(
            ...team.filter(isMobileLocalFriend).map((member) => member._id),
          );
      }
  
      if (autoInsertArt) {
          defaultData.pages = filteredImages.map((i) => ({ image: i }));
      } else {
          defaultData.pages = _.fill(Array(pagesCount), {});
      }
  
      // versionType
      /*const activityAssetsKey = versionType
          ? `${activitiesKey}/${activityId}/languageVersionData/${versionType}`
          : `${activitiesKey}/${activityId}/gamedata`;
  
      const activityAssetsFolder = `${uploadsURL}/${activityAssetsKey}/`;*/
  
      const learnerAssetsKey = learnerId
          ? `${usersKey}/${learnerId}/${activityId}`
          : '';
  
      const learnerAssetsFolder = learnerId
          ? `${uploadsURL}/${learnerAssetsKey}/`
          : '';
  
      this.state = {
          mode: storyStudioMode,
          gameData,
          paths: {
              activityAssetsKey,
              activityAssetsFolder,
              learnerAssetsKey,
              learnerAssetsFolder,
          },
          filteredWords,
          pagesCount,
          filteredImages,
          learnersIDs,
          localFriendIds,
          learnerAssetsList: [],
          data: defaultData,
          activePageIdx: 0,
          isLandscape: false,
          showEditor: true,
          ebookMode: false,
          initialSave: true,
          needToCopyAllImages: autoInsertArt,
          approved: false,
          mobileViewportReady: false,
          mobileViewportRevision: 0,
          showMobileInstructions: false,
      };
  
      this.getPagesText = this.getPagesText.bind(this);
  }
  

  componentDidMount() {
    document.addEventListener('startGame', this.init);

    const { learnersIDs } = this.state;

    // Get students data for this activity
    if (learnersIDs.length) {
      this.getLearnerData();
    }

    this.checkOrientation();
    this.bindMobileViewportSizing();
  }

  getPagesText() {
    const pagesContent = Array.from(this.state.data.pages.entries())
      .map(([index, page]) => {
        const text = extractContent(page?.text)?.trim();
        return { page: index + 1, text }
      })
      .filter(({ text }) => !!text);

    return pagesContent
      .map(({ text }) => text)
      .join("... ");
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.aspectRatio !== this.props.aspectRatio) {
      this.checkOrientation();
    }

    if (prevState.activePageIdx !== this.state.activePageIdx) {
      this.reloadEditor();
    }
  }

  componentWillUnmount() {
    stopAudio();
    document.removeEventListener('startGame', this.init);
    this.unbindMobileViewportSizing();
  }

  isAndroidPhoneRuntime = () => (
    isNativePhoneViewport(this.props.mobileLayout)
    && getMobileAppPlatform() === 'android'
  );

  captureMobileStableViewport = () => {
    if (!isNativePhoneViewport(this.props.mobileLayout)) return;

    const viewport = getViewportSize();
    if (viewport.width && viewport.height) {
      this.mobileStableViewport = viewport;
      if (this.isAndroidPhoneRuntime()) this.androidStableViewport = viewport;
    }
  };

  bindMobileViewportSizing = () => {
    if (!isNativePhoneViewport(this.props.mobileLayout)) return;

    this.captureMobileStableViewport();

    window.addEventListener('resize', this.handleMobileViewportChange);
    window.addEventListener('orientationchange', this.handleMobileViewportChange);
    window.visualViewport?.addEventListener('resize', this.handleMobileViewportChange);

    this.mobileViewportFrame = requestAnimationFrame(() => {
      this.mobileViewportFrame = requestAnimationFrame(() => {
        this.setState((state) => ({
          mobileViewportReady: true,
          mobileViewportRevision: state.mobileViewportRevision + 1,
        }));
      });
    });
  };

  unbindMobileViewportSizing = () => {
    window.removeEventListener('resize', this.handleMobileViewportChange);
    window.removeEventListener('orientationchange', this.handleMobileViewportChange);
    window.visualViewport?.removeEventListener('resize', this.handleMobileViewportChange);

    if (this.mobileViewportFrame) cancelAnimationFrame(this.mobileViewportFrame);
    if (this.mobileViewportTimer) clearTimeout(this.mobileViewportTimer);
    this.mobileViewportFrame = null;
    this.mobileViewportTimer = null;
  };

  handleMobileViewportChange = () => {
    if (!isNativePhoneViewport(this.props.mobileLayout)) return;

    const viewport = getViewportSize();
    const stableViewport = this.mobileStableViewport;

    if (!stableViewport && viewport.width && viewport.height) {
      this.mobileStableViewport = viewport;
      if (this.isAndroidPhoneRuntime()) this.androidStableViewport = viewport;
    } else if (stableViewport && viewport.width && viewport.height) {
      const widthDelta = Math.abs(viewport.width - stableViewport.width);
      const heightDelta = viewport.height - stableViewport.height;
      const keyboardLikeResize = widthDelta < 24 && heightDelta < -80;

      // iOS and Android both shrink visualViewport while the soft keyboard is
      // visible. Story Studio must keep its pre-keyboard canvas size/scale so the
      // editor does not suddenly zoom down when the learner taps into the text.
      if (keyboardLikeResize) return;

      if (widthDelta >= 24 || heightDelta > 40) {
        this.mobileStableViewport = viewport;
        if (this.isAndroidPhoneRuntime()) this.androidStableViewport = viewport;
      }
    }

    if (this.mobileViewportTimer) clearTimeout(this.mobileViewportTimer);
    this.mobileViewportTimer = setTimeout(() => {
      this.setState((state) => ({
        mobileViewportReady: true,
        mobileViewportRevision: state.mobileViewportRevision + 1,
      }));
    }, 60);
  };

  init = () => {
    document.dispatchEvent(new Event('playInstructionsAudio'));
  };

  onExit = (goBackFn) => {
    const {
      showStats,
      closeWritingFeedback,
      activity: {
        data: { options },
      },
    } = this.props;
    const { data, mode, approved } = this.state;

    closeWritingFeedback();
    this.setState({ exiting: true }, () => {
      const texts = _.chain(data.pages)
        .map(
          (p) =>
            p &&
            p.text &&
            p.text
              .replace(/<[\s\S]+?>/g, ' ')
              .replace(/[^\w\s]/g, '')
              .split(/\s+/)
              .filter((w) => !!w).length
        )
        .filter((t) => !!t)
        .sort((a, b) => b - a)
        .take(4)
        .value();

      const wordsCount = _.sum(texts);

      console.log(texts, wordsCount);

      const audiosNumber = data.pages
        .map((p) => p && p.audio)
        .filter((a) => !!a).length;

      let isMastered, isPartial;

      const textsMastered = texts.length === 4 && wordsCount >= 5;
      const textsPartial = texts.length >= 2 && wordsCount >= 3;

      const audioMastered = audiosNumber >= 4;
      const audioPartial = audiosNumber >= 2;

      if (mode === EDITOR_ONLY) {
        isMastered = textsMastered;
        isPartial = textsPartial;
      } else if (mode === RECORDER_ONLY) {
        isMastered = audioMastered;
        isPartial = audioPartial;
      } else {
        isMastered = audioMastered || textsMastered;
        isPartial = audioPartial || textsPartial;
      }

      if ((isMastered || isPartial) && (!options.needApproval || approved)) {
        showStats({
          predefinedMastered: isMastered,
          compactMobile: true,
        });

        this.saveChanges();
      } else {
        this.saveChanges(goBackFn);
      }

      // if (isMastered) {
      //   // If five words on two pages, then activity is mastered
      //   setActivityMastered();
      // } else if (isPartial) {
      //   // If two words on 1 page, then activity is partial mastered
      //   setActivityPartial();
      // }
    });
  };

  getLearnerData = async () => {
    const {
      activity: {
        activity: { _id: activityId },
        /*data: {
          gameData: { autoInsertArt },
        },*/
      },
      noReports,
    } = this.props;
    const { learnersIDs, gameData } = this.state;
    const { autoInsertArt } = gameData;

    if (!learnersIDs.length || noReports) return;

    const learnersData = await Promise.all(
      learnersIDs.map((learnerId) =>
        API.game.getLearnerActivityData({
          learnerId,
          activityId,
        })
      )
    );

    // If multiple members logged in, then check that they have same activity data
    const allDataIsTheSame = !!learnersData.reduce(
      (a, b) => JSON.stringify(a) === JSON.stringify(b)
    );

    if (!allDataIsTheSame) return;

    const data = learnersData[0];
    if (!data) return;

    this.getLearnerAssets();

    const { pagesCount } = this.state;

    const learnerPagesCount = data.pages && data.pages.length;

    if (learnerPagesCount < pagesCount) {
      data.pages.length = pagesCount;
    }

    this.setState(
      {
        data,
        needToCopyAllImages:
          autoInsertArt && (!data.pages || data.pages.length === 0),
      },
      this.reloadEditor
    );
  };

  getLearnerAssets = () => {
    const { activity, learnerId } = this.props;

    API.game
      .getLearnerActivityAssetsList({
        learnerId,
        activityId: activity.activity._id,
      })
      .then((list) => {
        this.setState({ learnerAssetsList: list });
      });
  };

  checkOrientation = () => {
    const { aspectRatio } = this.props;

    if (!aspectRatio) return;
    this.setState({ isLandscape: aspectRatio >= 1.33 });
  };

  changeActivePageIdx = (idx) => {
    this.setState({ activePageIdx: idx }, () => {
      // this.saveChanges();
    });
  };

  onEditorChange = (html) => {
    const { data = {}, activePageIdx, ebookMode } = this.state;
    const { pages = [] } = data;

    if (
      ebookMode ||
      (pages[activePageIdx] && pages[activePageIdx].text === html)
    ) {
      return;
    }

    this.changeCurrentPageValues({ text: html });
  };

  changeCurrentPageValues = (values, cb) => {
    const { data = {}, activePageIdx } = this.state;
    const { pages = [] } = data;

    const pagesCopy = JSON.parse(JSON.stringify(pages));

    pagesCopy[activePageIdx] = {
      ...(pagesCopy[activePageIdx] || {}),
      ...values,
    };

    this.setState(
      {
        hasUnsavedChanges: true,
        data: {
          ...data,
          pages: pagesCopy,
        },
      },
      cb
    );
  };

  saveChanges = async (cb) => {
    const { activity, learnerId, preview, noReports } = this.props;
    const {
      data,
      initialSave,
      hasUnsavedChanges,
      needToCopyAllImages,
      exiting,
      learnerAssetsList,
      learnersIDs,
      localFriendIds,
    } = this.state;

    if (preview || !learnerId || !hasUnsavedChanges || noReports) {
      return cb && cb();
    }

    const activityId = activity.activity._id;

    // Before Save
    // Check if team logged in, and this is a first save
    if (learnersIDs.length > 1 && initialSave) {
      // If so, then cleanup learners folders
      const results = await Promise.all(
        learnersIDs.map((learnerId) =>
          API.game
            .getLearnerActivityAssetsList({ learnerId, activityId })
            .then((assetsList) => {
              return API.game.deleteLearnerActivityAssets({
                learnerId,
                activityId,
                filenames: _.difference(assetsList, learnerAssetsList),
              });
            })
        )
      );

      console.log(results);
    }

    try {
      const success = await API.game.saveLearnerActivityData({
        learnerId: learnersIDs,
        activityId,
        data,
      });

      if (success && !exiting) {
        this.setState({ hasUnsavedChanges: false, initialSave: false });
      }

      if (needToCopyAllImages) {
        this.copyAllImagesToLearnersFolder();
      }

      localFriendIds.forEach((friendId) => {
        const friend = this.props.team?.find((member) => member._id === friendId);
        updateMobileFamilyFriend(friendId, {
          activityData: {
            ...(friend?.activityData || {}),
            [activityId]: data,
          },
        });
      });
    } catch (err) {
      console.log(err);
    }

    cb && cb();
  };

  reloadEditor = () => {
    this.setState({ showEditor: false }, () => {
      this.setState({ showEditor: true });
    });
  };

  copyAllImagesToLearnersFolder = () => {
    const {
      activity: {
        activity: { _id: activityId },
      },
      learnerId,
    } = this.props;
    const {
      learnersIDs,
      exiting,
      filteredImages,
      paths: { activityAssetsKey },
    } = this.state;

    if (!learnerId) return;

    const array = filteredImages.map((filename) => {
      return {
        srcKey: `${activityAssetsKey}/${filename}`,
        uploadBucket: true,
        filename,
      };
    });

    API.game
      .copyToLearnerActivityFolder({
        activityId,
        learnerId: learnersIDs,
        array,
      })
      .then((results) => {
        !exiting &&
          this.setState((state) => ({
            learnerAssetsList: [...state.learnerAssetsList, ...filteredImages],
            needToCopyAllImages: false,
          }));
      });
  };

  uploadAudio = (url, blob) => {
    const { learnerId, activity, noReports } = this.props;
    const { activePageIdx, learnersIDs } = this.state;
    const activityId = activity.activity._id;

    if (!learnerId || noReports) {
      return this.changeCurrentPageValues({ audio: url });
    }

    const filename = `page-${activePageIdx}-${shortid.generate()}.mp3`;

    return API.game
      .uploadToLearnerActivityAssets({
        activityId,
        learnerId: learnersIDs,
        files: [
          {
            file: blob,
            filename,
          },
        ],
      })
      .then((results) => {
        this.setState(
          (state) => ({
            learnerAssetsList: [...state.learnerAssetsList, filename],
          }),
          () =>
            this.changeCurrentPageValues({ audio: filename }, this.saveChanges)
        );
      });
  };

  deleteAudio = () => {
    const { learnerId, activity, noReports } = this.props;
    const { learnersIDs } = this.state;
    const activityId = activity.activity._id;

    if (!learnerId || noReports) {
      return this.changeCurrentPageValues({ audio: null });
    }

    const currentPage = this.getCurrentPage();

    if (!currentPage.audio) return;

    return API.game
      .deleteLearnerActivityAssets({
        activityId,
        learnerId: learnersIDs,
        filenames: [currentPage.audio],
      })
      .then((results) => {
        this.setState(
          (state) => ({
            learnerAssetsList: state.learnerAssetsList.filter(
              (f) => f !== currentPage.audio
            ),
          }),
          () => this.changeCurrentPageValues({ audio: null })
        );
      });
  };

  uploadImage = (filename, srcKey, fromClipart) => {
    const {
      activity: {
        activity: { _id: activityId },
      },
      learnerId,
      noReports,
    } = this.props;
    const { learnersIDs } = this.state;

    if (!learnerId || noReports) return;

    return API.game
      .copyToLearnerActivityFolder({
        activityId,
        learnerId: learnersIDs,
        array: [
          {
            srcKey,
            uploadBucket: !fromClipart,
            filename,
          },
        ],
      })
      .then((results) => {
        this.setState((state) => ({
          learnerAssetsList: [...state.learnerAssetsList, filename],
        }));
      });
  };

  deleteImage = (filename) => {
    const {
      activity: {
        activity: { _id: activityId },
      },
    } = this.props;
    const { learnersIDs } = this.state;

    API.game
      .deleteLearnerActivityAssets({
        activityId,
        learnerId: learnersIDs,
        filenames: [filename],
      })
      .then((success) => {
        if (success) {
          this.setState((state) => ({
            learnerAssetsList: state.learnerAssetsList.filter(
              (f) => f !== filename
            ),
          }));
        }
      });
  };

  onRemoveImage = () => {
    const { learnerId } = this.props;
    const { data = {}, activePageIdx, learnerAssetsList } = this.state;
    const { pages = [] } = data;

    const currentPage = this.getCurrentPage();

    const { image } = currentPage;
    if (!image) return;

    if (learnerId) {
      const isImageInUse = pages.reduce((inUse, page, idx) => {
        return inUse || (idx !== activePageIdx && page && page.image === image);
      }, false);

      if (!isImageInUse && learnerAssetsList.includes(image)) {
        this.deleteImage(image);
      }
    }

    this.changeCurrentPageValues({ image: '' }, this.saveChanges);
  };

  onChangeImage = async (newImageFilename, srcKey, fromClipart) => {
    const { learnerId, preview } = this.props;
    const { data = {}, activePageIdx, learnerAssetsList } = this.state;
    const { pages = [] } = data;

    if (preview && fromClipart) {
      return alert('Import from clipart not available in Preview');
    }

    const currentPage = pages[activePageIdx] || {};

    if (currentPage.image === newImageFilename) {
      return;
    }

    // Check if need to delete previous image
    const previousImage = currentPage.image;

    if (previousImage) {
      const isImageInUse = pages.reduce((inUse, page, idx) => {
        return (
          inUse ||
          (idx !== activePageIdx && page && page.image === previousImage)
        );
      }, false);

      if (
        learnerId &&
        !isImageInUse &&
        learnerAssetsList.includes(previousImage)
      ) {
        this.deleteImage(previousImage);
      }
    }

    // Check if need to upload new image
    if (learnerId && !learnerAssetsList.includes(newImageFilename)) {
      await this.uploadImage(newImageFilename, srcKey, fromClipart);
    }

    this.changeCurrentPageValues({ image: newImageFilename }, this.saveChanges);
  };

  startPreview = () => {
    const {
      mode,
      data: { pages },
    } = this.state;

    const validPages = pages.filter(
      (p) => p && (p.text || p.image || (mode !== EDITOR_ONLY && p.audio))
    );

    this.setState(
      {
        ebookMode: true,
        validPages,
        activePageIdx: 0,
      },
      () => {
        this.saveChanges();
        this.playPageAudio();
      }
    );
  };

  stopPreview = () => {
    stopAudio();
    this.setState({ ebookMode: false, activePageIdx: 0, validPages: [] });
  };

  playPageAudio = () => {
    const { mode, ebookMode } = this.state;
    if (!ebookMode || mode === EDITOR_ONLY) return;

    const url = this.getCurrentAudioUrl();
    if (!url) return;

    console.log(url);

    playAudio(url, { onEnd: this.showNextPage, format: 'mp3' });
  };

  showPrevPage = () => {
    const { activePageIdx } = this.state;
    const isFirstPage = activePageIdx === 0;

    if (!isFirstPage) {
      this.setState({ activePageIdx: activePageIdx - 1 }, this.playPageAudio);
    }
  };

  showNextPage = () => {
    const {
      activePageIdx,
      ebookMode,
      data: { pages },
      validPages,
    } = this.state;

    const currentPages = ebookMode ? validPages : pages;

    const isLastPage = currentPages.length === activePageIdx + 1;

    if (currentPages.length && !isLastPage) {
      this.setState({ activePageIdx: activePageIdx + 1 }, this.playPageAudio);
    }
  };

  getCurrentPage = () => {
    const {
      ebookMode,
      validPages,
      activePageIdx,
      data: { pages },
    } = this.state;
    const currentPages = ebookMode ? validPages : pages;
    const activePage = currentPages[activePageIdx] || {};

    return activePage;
  };

  getCurrentAudioUrl = () => {
    const { learnerId } = this.props;
    const {
      paths: { learnerAssetsFolder },
    } = this.state;

    const currentPage = this.getCurrentPage();

    let url = currentPage.audio;

    if (!url) return;

    if (learnerId) {
      url = learnerAssetsFolder + url;
    }

    return url;
  };

  toggleApprove = () => {
    this.setState({ approved: !this.state.approved });
  };

  toggleMobileInstructions = () => {
    this.setState((state) => ({
      showMobileInstructions: !state.showMobileInstructions,
    }));
  };

  closeMobileInstructions = () => {
    this.setState({ showMobileInstructions: false });
  };

  render() {
    const {
      mode,
      paths,
      approved,
      ebookMode,
      pagesCount,
      validPages,
      learnerAssetsList,
      activePageIdx,
      isLandscape,
      filteredImages,
      showEditor,
      filteredWords,
      data: { pages },
      gameData,
      showMobileInstructions,
    } = this.state;
    const {
      solutionLocale,
      activity,
      translate,
      getStringAudio,
      mobileLayout,
    } = this.props;
    const {
      data: { options },
    } = activity;
console.log('StoryStudio solutionLocale: ',solutionLocale);
    const currentPages = ebookMode ? validPages : pages;

    const activePage = currentPages[activePageIdx] || {};
    const hidePageSelector = this.props.workspaceSnapshots ? pages.length <= 1 : this.props.hidePageSelector;

    const { importClipart = false, autoInsertArt = false } = gameData;
    const instruction = getInstruction(
      gameData, 
      solutionLocale,
      undefined, undefined, undefined,
      { translate, getStringAudio },
    );

    const currentImage = activePage.image;
    const showPictureBox =
      !ebookMode &&
      ((filteredImages.length && !autoInsertArt) ||
        importClipart ||
        currentImage || this.props.reserveImageList);

    const isOnlyRecorder =
      mode === RECORDER_ONLY ||
      (mode === EDITOR_AND_RECORDER && activePage.audio);

    const showRecorder =
      !ebookMode && (mode === RECORDER_ONLY || mode === EDITOR_AND_RECORDER);

    const showEditorArea = showEditor && !isOnlyRecorder;

    const rawCurrentImageUrl =
      currentImage &&
      (learnerAssetsList.includes(currentImage)
        ? paths.learnerAssetsFolder
        : paths.activityAssetsFolder) + currentImage;

    const isPageHasAudio = !!activePage.audio;
    const nativePhoneViewport = isNativePhoneViewport(mobileLayout);
    const iosRuntime = getMobileAppPlatform() === 'ios';
    const currentImageUrl = (nativePhoneViewport || iosRuntime) && rawCurrentImageUrl
      ? resolveOfflineAssetUrlSync(rawCurrentImageUrl)
      : rawCurrentImageUrl;
    const mobilePhoneLayout = nativePhoneViewport && !ebookMode;
    const mobilePlatform = mobilePhoneLayout ? getMobileAppPlatform() : 'web';
    const androidPhoneLayout = mobilePhoneLayout && mobilePlatform === 'android';
    const mobileStableViewport = mobilePhoneLayout ? this.mobileStableViewport : null;
    const androidStableViewport = androidPhoneLayout ? this.androidStableViewport : null;
    const phoneWorkAreaHeight = STORY_STUDIO_PHONE_WORK_AREA_HEIGHT
      - (androidPhoneLayout ? STORY_STUDIO_ANDROID_HEIGHT_REDUCTION : 0);
    const phonePictureHeightWithRecorder = STORY_STUDIO_PHONE_PICTURE_HEIGHT_WITH_RECORDER
      - (androidPhoneLayout ? STORY_STUDIO_ANDROID_HEIGHT_REDUCTION : 0);
    const androidWrapperTop = androidPhoneLayout && androidStableViewport?.height
      ? (androidStableViewport.height * 0.46) + STORY_STUDIO_ANDROID_TOP_OFFSET + 'px'
      : undefined;
    const layoutScale = mobilePhoneLayout
      ? getStoryStudioPhoneScale(this.state.mobileViewportReady, mobileStableViewport)
      : (nativePhoneViewport
        ? getStoryStudioEbookPhoneScale(this.state.mobileViewportReady)
        : getScale());
    const editorLeft = showPictureBox ? 264 : 0;
    const picturePanelLeft = 0;
    const picturePanelWidth = 252;
    const pagesPanelLeft = mobilePhoneLayout
      ? STORY_STUDIO_PHONE_WIDTH - STORY_STUDIO_PHONE_PAGES_PANEL_WIDTH
      : 936;
    const editorWidth = mobilePhoneLayout
      ? pagesPanelLeft - STORY_STUDIO_PHONE_PANEL_GAP - editorLeft
      : (showPictureBox ? 660 : 924);
    const activityButtons = (
      <ActivityButtons
        buttons={[
          { type: GO_BACK, beforeClickHandler: this.onExit },
          {
            type: ACTIVITY_IMAGE,
            onClick: this.toggleMobileInstructions,
            dontShow: !mobilePhoneLayout || !instruction.text,
            titleStringNumber: 432,
            titleName: 'Instructions',
          },
          {
            type: EBOOK_BUTTON,
            dontShow: ebookMode,
            onClick: this.startPreview,
          },
          {
            type: STOP,
            dontShow: !ebookMode,
            onClick: this.stopPreview,
          },
          {
            type: SPEAK,
            dontShow: !instruction.audio || ebookMode,
          },
          HELP,
        ]}
      />
    );

    return (
      <>
        {!ebookMode && (
          mobilePhoneLayout ? (
            <div className={classes.mobileInstructionAudioBridge} aria-hidden="true">
              <Instructions
                problem={gameData}
                autoPlay={false}
              />
            </div>
          ) : (
            <Instructions
              problem={gameData}
              autoPlay={false}
              wrapperStyle={{
                top: '1%',
                marginLeft: (65 / 2) * layoutScale + 'px',
                width: (1858 / 2) * layoutScale + 'px',
              }}
            />
          )
        )}

        <div
          style={{
            top: androidWrapperTop,
            height: mobilePhoneLayout
              ? STORY_STUDIO_PHONE_HEIGHT + 'px'
              : divideSize(1106),
            width: mobilePhoneLayout
              ? STORY_STUDIO_PHONE_WIDTH + 'px'
              : divideSize(1988),
            transform: 'translate(-50%, -50%) scale(' + layoutScale + ')',
          }}
          className={classnames(classes.wrapper, {
            'math-writing-layout': this.props.workspaceSnapshots && mobileLayout && !ebookMode,
            [classes.withPictureBox]: showPictureBox,
            [classes.mobilePhoneWrapper]: mobilePhoneLayout,
            [classes.desktopWrapper]: !nativePhoneViewport,
          })}
        >
          {!ebookMode && !hidePageSelector && (
            <WhiteBox
              outerStyle={mobilePhoneLayout ? {
                position: 'absolute',
                top: 0,
                left: pagesPanelLeft + 'px',
                width: STORY_STUDIO_PHONE_PAGES_PANEL_WIDTH + 'px',
                height: phoneWorkAreaHeight + 'px',
                fontSize: '36px',
                padding: '8px',
                borderRadius: '18px',
                boxSizing: 'border-box',
              } : {
                position: 'absolute',
                top: 0,
                left: divideSize(0),
                width: divideSize(100),
                height: divideSize(700),
                fontSize: divideSize(40),
                padding: '15px',
                borderRadius: '30px',
              }}
              outerClass={classnames(classes.pagesListWrapper, {'math-writing-pages':this.props.workspaceSnapshots && mobileLayout})}
              innerClass={classes.pagesListInner}
            >
              <PagesListBox
                pagesCount={this.props.workspaceSnapshots ? pages.length : isNaN(pagesCount) ? 0 : +pagesCount}
                activePage={activePageIdx}
                onChange={this.changeActivePageIdx}
              />
            </WhiteBox>
          )}

          <WhiteBox
            outerStyle={mobilePhoneLayout ? {
              top: 0,
              left: editorLeft + 'px',
              width: editorWidth + 'px',
              height: phoneWorkAreaHeight + 'px',
              minHeight: phoneWorkAreaHeight + 'px',
              padding: '10px',
              borderRadius: '22px',
              boxSizing: 'border-box',
            } : {
              top: ebookMode ? -50 : 0,
              width: divideSize(1858),
              left: divideSize(ebookMode || hidePageSelector ? 65 : 130),
              height: ebookMode ? 'auto' : divideSize(700),
              minHeight: divideSize(700),
              padding: '15px',
              borderRadius: '30px',
            }}
            outerClass={classnames(classes.editorWrapper, {
              'math-writing-editor':this.props.workspaceSnapshots && mobileLayout && !ebookMode,
              [classes.editorEbookMode]: ebookMode,
            })}
            innerClass={classes.editorInner}
          >
            {showEditorArea && (
              <StoryStudioEditor
                readOnly={ebookMode}
                words={filteredWords}
                toolbar={options.editor}
                initialValue={activePage.text}
                changeHandler={this.onEditorChange}
                isLandscape={isLandscape}
                writingFeedbackEnabled={!mobilePhoneLayout && options.aiFeedbackEnabled !== false}
                getPagesText={this.getPagesText}
                preventProgrammaticFocus={mobilePhoneLayout}
                preventWordSelectionFocus={androidPhoneLayout}
              />
            )}

            {((!ebookMode && isOnlyRecorder) ||
              (ebookMode && currentImageUrl)) && (
                <div
                  className={classnames(classes.currentImage, {
                    [classes.big]: isOnlyRecorder,
                  })}
                >
                  <img src={currentImageUrl} alt="" />
                </div>
              )}

            {ebookMode && !isPageHasAudio && (
              <>
                {activePageIdx !== 0 && (
                  <button
                    onClick={this.showPrevPage}
                    className={classnames(classes.navBtn, classes.prevBtn)}
                  >
                    <img src={prevBtnImg} alt="Previous" />
                  </button>
                )}

                {currentPages.length !== 0 &&
                  activePageIdx !== currentPages.length - 1 && (
                    <button
                      onClick={this.showNextPage}
                      className={classnames(classes.navBtn, classes.nextBtn)}
                    >
                      <img src={nextBtnImg} alt="Next" />
                    </button>
                  )}
              </>
            )}
          </WhiteBox>

          <div
            className={classnames(classes.bottomSectionWrapper, {'math-writing-media':this.props.workspaceSnapshots && mobileLayout && !ebookMode})}
            style={mobilePhoneLayout ? {
              top: showPictureBox ? 0 : '394px',
              left: showPictureBox ? picturePanelLeft + 'px' : '50%',
              width: showPictureBox ? picturePanelWidth + 'px' : '360px',
              height: showPictureBox ? phoneWorkAreaHeight + 'px' : '52px',
              transform: showPictureBox ? 'none' : 'translateX(-50%)',
              flexDirection: showPictureBox ? 'column' : 'row',
              alignItems: 'stretch',
              gap: '8px',
            } : {
              height: divideSize(376),
              top: divideSize(720),
            }}
          >
            {showPictureBox && (
              <WhiteBox
                outerStyle={mobilePhoneLayout ? {
                  width: picturePanelWidth + 'px',
                  height: showRecorder
                    ? phonePictureHeightWithRecorder + 'px'
                    : phoneWorkAreaHeight + 'px',
                  padding: '8px',
                  borderRadius: '20px',
                  boxSizing: 'border-box',
                } : {
                  width: this.props.preserveCurrentImageSize && this.state.writingImageWidth
                    ? Math.min(929, Math.max(570, this.state.writingImageWidth / layoutScale * 2 + 60)) + 'px'
                    : divideSize(1140),
                  height: this.props.allowRemoveCurrent ? divideSize(376) : undefined,
                  padding: '15px',
                  borderRadius: '30px',
                }}
                outerClass={classnames(classes.pictureBoxWrapper, {'math-writing-picture':this.props.workspaceSnapshots && mobileLayout})}
                innerStyle={{
                  padding: 0,
                  display: 'flex',
                }}
              >
                <PictureBox
                  paths={paths}
                  showList={
                    (!!filteredImages.length && !autoInsertArt) || importClipart || this.props.reserveImageList
                  }
                  images={filteredImages}
                  workspacePages={this.props.workspaceSnapshots ? pages : undefined}
                  activeWorkspacePage={activePageIdx}
                  onSelectWorkspacePage={this.changeActivePageIdx}
                  learnerAssetsList={learnerAssetsList}
                  onChangeImage={this.onChangeImage}
                  onRemoveImage={this.onRemoveImage}
                  currentImage={activePage.image}
                  allowRemoveCurrent={this.props.allowRemoveCurrent}
                  preserveCurrentImageSize={this.props.preserveCurrentImageSize}
                  dividedImageBox={this.props.dividedImageBox}
                  currentImageScale={layoutScale}
                  onCurrentImageLoad={this.props.preserveCurrentImageSize ? image => {
                    if (this.state.writingImageWidth !== image.naturalWidth) this.setState({writingImageWidth:image.naturalWidth});
                  } : undefined}
                  withClipart={importClipart}
                  maxImages={mobilePhoneLayout ? 8 : 12}
                  mobilePhoneLayout={mobilePhoneLayout}
                />
              </WhiteBox>
            )}

            {showRecorder && (
              <PlayRecorder
                currentUrl={this.getCurrentAudioUrl()}
                uploadHandler={this.uploadAudio}
                deleteHandler={this.deleteAudio}
                classes={{
                  wrapper: classes.recorder,
                  inner: classes.recorderInner,
                  button: classes.recorderButton,
                }}
              />
            )}

            {options.needApproval && (
              <div className={classnames(classes.approvalCheckbox)}>
                <input
                  checked={approved}
                  onChange={this.toggleApprove}
                  id="approval-checkbox"
                  type="checkbox"
                />
                <label htmlFor="approval-checkbox">
                  {translate(850, 'Approval')}
                </label>
              </div>
            )}
          </div>
        </div>

        {mobilePhoneLayout && showMobileInstructions && instruction.text && (
          <MathRodsGameModal
            title={translate(432, 'Instructions')}
            mobile={!!this.props.workspaceSnapshots}
            outerClassnames={[classes.mobileInstructionModal]}
            innerClassnames={[classes.mobileInstructionModalInner]}
            contentClassnames={[classes.mobileInstructionModalContent]}
            titleClassnames={[classes.mobileInstructionModalTitle]}
            buttonsClassnames={[classes.mobileInstructionModalButtons]}
            buttons={[
              {
                icon: `${imagesURL}/Images/Help/Close50.png`,
                clickHandler: this.closeMobileInstructions,
              },
            ]}
          >
            <div className={classes.mobileInstructionText}>
              {instruction.text}
            </div>
          </MathRodsGameModal>
        )}

        {!nativePhoneViewport && <WritingFeedback />}

        {nativePhoneViewport ? (
          <div className={classes.mobileGlobalButtonsLayer}>
            {activityButtons}
          </div>
        ) : activityButtons}
      </>
    );
  }
}

StoryStudio.propTypes = {
  aspectRatio: PropTypes.number,
  versionType: PropTypes.string,
  learnerId: PropTypes.string,
  team: PropTypes.array,
  noReports: PropTypes.bool,
  mobileLayout: PropTypes.bool,
  allowRemoveCurrent: PropTypes.bool,
};

const mapStateToProps = ({ flinkPlay: { learner, team, learningCenter } }) => ({
  learnerId: learner && learner._id,
  noReports: !!(
    learningCenter &&
    learningCenter.config &&
    learningCenter.config.noReports
  ),
  team,
});

export default connect(mapStateToProps, {
  showStats,
  closeWritingFeedback,
})(StoryStudio);
